// Themed confirm modal — replaces the OS confirm() dialog so the
// "Delete list?" / "Clear done?" prompts respect dark/accent/density
// tokens.
//
// Imperative API: `await confirm({ title, message, confirmLabel,
// danger })` resolves true on confirm, false on cancel/close. A
// module-level subscriber is mounted once at app root and listens
// for opens through the singleton store. This shape keeps call sites
// terse — `if (await confirm({...})) doIt();` — without each caller
// having to thread modal state.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../icons";

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

let _setPending: ((p: Pending | null) => void) | null = null;

/** Call from anywhere. Resolves true on confirm, false otherwise. */
export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!_setPending) {
      // Mount race fallback — keeps the API safe to call before the
      // host has rendered ConfirmHost. Behaves like native window.confirm
      // so the UI never accidentally proceeds without a user yes.
      resolve(typeof window !== "undefined"
        ? window.confirm((opts.title ? opts.title + "\n\n" : "") + opts.message)
        : false);
      return;
    }
    _setPending({ ...opts, resolve });
  });
}

/** Mount once at the app root (already done in app.tsx). */
export function ConfirmHost() {
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    _setPending = setPending;
    return () => { _setPending = null; };
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
      else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) confirmOk();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  if (!pending) return null;
  const danger = !!pending.danger;
  const cancel = () => { pending.resolve(false); setPending(null); };
  const confirmOk = () => { pending.resolve(true); setPending(null); };

  return (
    <div className="confirm-bg" onClick={cancel}>
      <div
        className="confirm-modal"
        data-tone={danger ? "danger" : "default"}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
      >
        <header className="confirm-head">
          <span className="confirm-icon">
            <Icon name={danger ? "warn" : "info"} size={14} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {pending.title && <div className="confirm-title">{pending.title}</div>}
          </div>
          <button className="icon-btn" onClick={cancel} title="Cancel" type="button">
            <Icon name="x" size={13} />
          </button>
        </header>
        <p className="confirm-message">{pending.message}</p>
        <div className="confirm-actions">
          <button className="btn" onClick={cancel} type="button">
            {pending.cancelLabel || "Cancel"}
          </button>
          <button
            className={danger ? "btn confirm-danger" : "btn btn-primary"}
            onClick={confirmOk}
            type="button"
            autofocus
          >
            {pending.confirmLabel || (danger ? "Delete" : "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
